import { Link } from "react-router-dom";
import {
  Users,
  Award,
  Heart,
  Linkedin,
  Sparkles,
  TrendingUp,
} from "lucide-react";
// import { PodFormation } from '../components/PodFormation';
import { PageHero } from "../components/PageHero";

export const About = () => {
  const leadership = [
    {
      name: "Pearl D. Gakazi",
      role: "CEO",
      image: "/images/pearl-gakazi.png",
      social: {
        linkedin: "https://www.linkedin.com/in/pearlgakazi/",
      },
    },
    {
      name: "Calvin Magezi",
      role: "CTO",
      image: "/images/calvin-magezi.png",
      social: {
        linkedin: "https://calvinmagezi.vercel.app/",
      },
    },
    {
      name: "Dianah Mutagoma",
      role: "COO",
      image: "/images/dianah-mutagoma.png",
      social: {
        linkedin: "https://www.linkedin.com/in/dianah-mutagoma-735847a0/",
      },
    },
  ];

  const values = [
    {
      icon: Award,
      title: "Honor",
      description:
        "We uphold the dignity of every professional and the integrity of every project, fostering a culture of respect and excellence.",
      color: "from-green-600 to-green-700",
    },
    {
      icon: Heart,
      title: "Relationships",
      description:
        "We believe growth comes from strong, lasting partnerships. We invest in deeply connected relationships with our clients and talents.",
      color: "from-green-600 to-green-700",
    },
    {
      icon: Users,
      title: "People",
      description:
        "Our community is our strength. We are committed to the growth, well-being, and empowerment of the people who drive African excellence.",
      color: "from-yellow-400 to-yellow-500",
    },
    {
      icon: Sparkles,
      title: "Value",
      description:
        "We drive measurable impact by combining efficiency and innovation to fuel sustainable outcomes for businesses globally.",
      color: "from-green-600 to-green-700",
    },
    {
      icon: TrendingUp,
      title: "Kaizen",
      description:
        "We are dedicated to learning, adapting, and refining our craft every single day ,and continuous improvements leading to massive excellence.",
      color: "from-orange-500 to-red-600",
    },
  ];

  const milestones = [
    {
      year: "2022",
      title: "Foundation in Kampala",
      description:
        "Pearl D. Gakazi founded Kolaborate in Kampala, Uganda, with a vision to connect African talent with global opportunities.",
      icon: "🚀",
    },
    {
      year: "2023",
      title: "Strategic Partnerships",
      description:
        "Secured grant funding from International Trade Centre, Jobtech Innovation Lab, Africa Tech Startup Forum, and Jobtech Alliance.",
      icon: "🤝",
    },
    {
      year: "2024",
      title: "Regional Expansion",
      description:
        "Extended services across Uganda, Kenya, Rwanda, and DRC, building a network of vetted professionals in multiple specializations.",
      icon: "🌍",
    },
    {
      year: "2024",
      title: "terratalent Partnership Launch",
      description:
        "Established strategic partnership with terratalent to connect African data analysts with German companies, creating direct European market access.",
      icon: "🇩🇪",
    },
    {
      year: "2025",
      title: "Training Program Success",
      description:
        "Graduated 40+ professionals from Uganda, Rwanda, and DRC in specialized data management, digital marketing, and IT outsourcing programs.",
      icon: "🎓",
    },
    {
      year: "2025",
      title: "Continued Growth",
      description:
        "Maintaining 32% placement success rate while expanding our portfolio of programming, web development, digital marketing, and BPO services.",
      icon: "📈",
    },
  ];

  const stats = [
    { number: "40+", label: "Latest Training Graduates", icon: "🎓" },
    { number: "4", label: "Key Global Partners", icon: "🤝" },
    { number: "3", label: "East African Countries", icon: "🏢" },
    { number: "2022", label: "Founded in Kampala", icon: "📍" },
  ];

  return (
    <div>
      <PageHero
        title="Building Bridges Between Africa & The World"
        subtitle="Founded in 2022 by Pearl D. Gakazi in Kampala, Uganda, Kolaborate connects African talent with global opportunities. Our strategic terratalent partnership has created direct pathways for African professionals into German markets, with 40+ recent graduates ready to contribute to international projects."
      />

      {/* Stats Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="text-center animate-fadeInUp"
                style={{ animationDelay: `${index * 0.1} s` }}
              >
                <div className="text-4xl mb-4">{stat.icon}</div>
                <div className="text-3xl font-bold text-brand-grey mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="animate-fadeInLeft">
              <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-xl">
                <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center text-white text-2xl font-bold mb-6">
                  🎯
                </div>
                <h2 className="text-3xl font-bold text-brand-grey mb-6">
                  Our Mission
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  We bridge the global talent gap through African excellence.
                  Our 4-step process (Sign up → Post requirements → Connect →
                  Manage projects) connects businesses with vetted professionals
                  in programming, web development, digital marketing, graphic
                  design, and BPO services.
                </p>
                <div className="border-l-4 border-brand-primary pl-4">
                  <p className="text-brand-primary font-medium">
                    "We honor talent, create value, and build lasting
                    relationships that transform lives and businesses globally."
                  </p>
                </div>
              </div>
            </div>

            <div className="animate-fadeInRight">
              <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-xl">
                <div className="w-16 h-16 bg-brand-secondary rounded-2xl flex items-center justify-center text-2xl font-bold mb-6">
                  🌟
                </div>
                <h2 className="text-3xl font-bold text-brand-grey mb-6">
                  Our Vision
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  Making Africa the go-to region for specialized tech talent by
                  showcasing the exceptional capabilities of professionals from
                  Uganda, Kenya, Rwanda, and DRC. We're building sustainable
                  pathways that benefit both African professionals and global
                  businesses.
                </p>
                <div className="border-l-4 border-brand-primary pl-4">
                  <p className="text-brand-primary font-medium">
                    "Making African BPO the future of work through honor, value,
                    and relationships."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fadeInUp">
            <h2 className="text-4xl font-bold text-brand-primary mb-6">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide our work and define our commitment to
              excellence, community, and global impact.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
              >
                <div
                  className={`absolute inset - 0 bg - gradient - to - br ${value.color} opacity - 0 group - hover: opacity - 5 rounded - 3xl transition - opacity duration - 500`}
                ></div>

                <div
                  className={`w - 16 h - 16 ${value.color.includes("green") ? "bg-green-600/20" : value.color.includes("orange") ? "bg-orange-500/20" : "bg-brand-yellow/20"} rounded - 2xl flex items - center justify - center mb - 6 group - hover: scale - 110 transition - transform duration - 300`}
                >
                  <value.icon
                    className={`w - 8 h - 8 bg - gradient - to - r ${value.color} bg - clip - text text - transparent`}
                    style={{ stroke: `url(#gradient - ${index})` }}
                  />
                  <svg width="0" height="0">
                    <defs>
                      <linearGradient
                        id={`gradient - ${index} `}
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="100%"
                      >
                        <stop
                          offset="0%"
                          stopColor={
                            value.color.includes("blue")
                              ? "#2563eb"
                              : value.color.includes("green")
                                ? "#059669"
                                : value.color.includes("purple")
                                  ? "#9333ea"
                                  : value.color.includes("pink")
                                    ? "#db2777"
                                    : value.color.includes("orange")
                                      ? "#f97316"
                                      : "#fbdf64"
                          }
                        />
                        <stop
                          offset="100%"
                          stopColor={
                            value.color.includes("blue")
                              ? "#4f46e5"
                              : value.color.includes("green")
                                ? "#10b981"
                                : value.color.includes("purple")
                                  ? "#ec4899"
                                  : value.color.includes("pink")
                                    ? "#be185d"
                                    : value.color.includes("orange")
                                      ? "#dc2626"
                                      : "#eab308"
                          }
                        />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>

                <h3 className="text-2xl font-bold mb-4 text-gray-800">
                  {value.title}
                </h3>

                <p className="text-gray-600 leading-relaxed">
                  {value.description}
                </p>

                <div
                  className={`absolute bottom - 0 left - 0 right - 0 h - 1 bg - gradient - to - r ${value.color} rounded - b - 3xl opacity - 0 group - hover: opacity - 100 transition - opacity duration - 500`}
                ></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fadeInUp">
            <div className="inline-flex items-center px-6 py-3 rounded-full bg-brand-secondary border border-gray-200 text-brand-grey text-sm font-semibold mb-6">
              <Users className="w-4 h-4 mr-2" />
              Leadership Excellence
            </div>
            <h2 className="text-4xl font-bold text-brand-primary mb-6">
              Meet Our Leadership Team
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experienced leaders driving our vision of showcasing African
              excellence on the global stage.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {leadership.map((member, index) => (
              <div
                key={index}
                className="group bg-white border border-gray-200 rounded-3xl p-8 shadow-lg hover:shadow-2xl text-center transition-all duration-500 transform hover:-translate-y-2 animate-fadeInUp"
                style={{ animationDelay: `${index * 0.1} s` }}
              >
                <div className="relative inline-block mb-6">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-32 h-32 rounded-3xl object-cover shadow-lg group-hover:scale-105 transition-transform duration-300"
                  />
                </div>

                <h3 className="text-xl font-bold text-brand-grey mb-2">
                  {member.name}
                </h3>
                <p className="text-brand-primary font-semibold mb-6">
                  {member.role}
                </p>

                <a
                  href={member.social.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-brand-primary hover:bg-brand-tertiary text-white rounded-lg transition-colors duration-300 text-sm font-medium"
                >
                  <Linkedin size={16} className="mr-2" />
                  Connect on LinkedIn
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Journey/Timeline */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-green-600/10"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16 animate-fadeInUp">
            <h2 className="text-4xl font-bold text-white mb-6">
              Our{" "}
              <span className="bg-gradient-to-r from-green-400 to-green-500 bg-clip-text text-transparent">
                Journey
              </span>{" "}
              So Far
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              From humble beginnings to global recognition - the milestones that
              shaped our mission.
            </p>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-green-500 to-green-700 rounded-full hidden lg:block"></div>

            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div
                  key={index}
                  className={`flex items - center ${index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"} animate - fadeInUp`}
                  style={{ animationDelay: `${index * 0.1} s` }}
                >
                  <div
                    className={`lg: w - 1 / 2 ${index % 2 === 0 ? "lg:pr-12" : "lg:pl-12"} `}
                  >
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/20 transition-all duration-300">
                      <div className="flex items-center mb-4">
                        <div className="text-3xl mr-4">{milestone.icon}</div>
                        <div className="text-2xl font-bold text-green-400">
                          {milestone.year}
                        </div>
                      </div>
                      <h3 className="text-xl font-bold text-white mb-3">
                        {milestone.title}
                      </h3>
                      <p className="text-gray-300 leading-relaxed">
                        {milestone.description}
                      </p>
                    </div>
                  </div>

                  {/* Timeline Node */}
                  <div className="hidden lg:block relative z-10">
                    <div className="w-6 h-6 bg-gradient-to-r from-green-500 to-green-700 rounded-full border-4 border-gray-900"></div>
                  </div>

                  <div className="lg:w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="/images/cta-1.jpeg"
            alt="African professionals collaborating"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-brand-primary/90 via-brand-tertiary/85 to-brand-primary/90"></div>
        </div>

        {/* Floating Elements */}
        <div className="absolute top-10 left-10 w-20 h-20 bg-white/10 rounded-full animate-float"></div>
        <div
          className="absolute bottom-10 right-10 w-16 h-16 bg-white/10 rounded-full animate-float"
          style={{ animationDelay: "1s" }}
        ></div>
        <div
          className="absolute top-1/2 left-1/4 w-12 h-12 bg-white/10 rounded-full animate-float"
          style={{ animationDelay: "2s" }}
        ></div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="animate-fadeInUp">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Build Something Great?
            </h2>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Work with talented people who care about getting things done
              right. Let's talk about your next project.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link
                to="/contact"
                className="bg-white text-green-600 px-8 py-4 rounded-2xl text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
              >
                Get Started
              </Link>
              <Link
                to="/portfolio"
                className="border-2 border-white text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-white hover:text-green-600 transition-all duration-300 transform hover:-translate-y-1"
              >
                See Our Work
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
